import { PaymentMethod } from '../exports/Constants';

/**
 * Why an order carries the method it does. Sent as `payment_method_source` so the backend can
 * tell a cash order the customer chose from one nobody chose.
 */
export type PaymentMethodSource = 'explicit' | 'selected' | 'free_order' | 'adyen_dropin';

export interface CheckoutPaymentMethodInput {
  /** A method the caller names for this submit (the Adyen Drop-in, PayPal-direct, the free-order CTA). */
  explicit?: null | PaymentMethod;
  explicitSource?: null | PaymentMethodSource;
  /** The customer's selection, already resolved for PayPal-direct. */
  selected?: null | PaymentMethod;
  /** The draft is zero AND current: see `isFreeOrderDraft`. */
  isFreeOrder: boolean;
}

export interface CheckoutPaymentMethod {
  method: null | PaymentMethod;
  source: null | PaymentMethodSource;
}

/**
 * The one place that decides which method an order is placed with.
 *
 * Cash is only ever a fallback for an order that costs nothing: the backend requires a method, and
 * cash is the only one a free order can be placed with. For anything else a missing method is
 * `null`, and the caller asks the customer to choose one instead of charging them cash.
 */
export function resolveCheckoutPaymentMethod({
  explicit,
  explicitSource,
  selected,
  isFreeOrder,
}: CheckoutPaymentMethodInput): CheckoutPaymentMethod {
  if (explicit) {
    return { method: explicit, source: explicitSource ?? 'explicit' };
  }

  if (selected) {
    return { method: selected, source: 'selected' };
  }

  if (isFreeOrder) {
    return { method: PaymentMethod.Cash, source: 'free_order' };
  }

  return { method: null, source: null };
}

/**
 * A zero total only means "free" when it belongs to the draft that matches the order right now. A
 * failed or superseded draft keeps its old total, which may be a zero from before the basket changed.
 */
export function isFreeOrderDraft(total: null | number | undefined, isDraftCurrent: boolean): boolean {
  return total === 0 && isDraftCurrent;
}
